// Copyright (c) Docugami, Inc. All rights reserved.

import { Config } from "config";
import { Telemetry } from "telemetry/TelemetryManager";

interface ErrorBucketSettings {
    rateLimitMs?: number;
    rateLimitCount?: number;
    singleInstance?: boolean;
}

const defaultSettings: ErrorBucketSettings = {
    singleInstance: true
}

export interface IErrorLimiter {
    handle(error: any): void;
    flush(): void;
}

export class ErrorLimiter implements IErrorLimiter {
    private readonly _settings: ErrorBucketSettings;
    private _buckets: Array<ErrorBucket> = [];
    private readonly _isDevMode: boolean;

    constructor(settings: Partial<ErrorBucketSettings> = {}, devModeOverride?: boolean) {
        this._settings = {
            ...defaultSettings,
            ...settings
        };

        this._isDevMode = devModeOverride !== undefined ? devModeOverride : Config.sdvDevMode;
    }

    handle(error: any): void {
        if (this._isDevMode) {
            throw error;
        }

        let targetBucket: ErrorBucket | null = null;
        for (const bucket of this._buckets) {
            if (bucket.handle(error)) {
                targetBucket = bucket;
                break;
            }
        }

        if (!targetBucket) {
            // Create a new bucket
            targetBucket = new ErrorBucket(error, this._settings);
            this._buckets.push(targetBucket);
        }

        this.removeDumpedBuckets();
    }

    flush(): void {
        this._buckets.forEach(b => b.dump());
        this._buckets = [];
    }

    private removeDumpedBuckets() {
        this._buckets = this._buckets.filter(x => !x.dumped);
    }
}

class ErrorBucket {
    private readonly _settings: ErrorBucketSettings;
    private _archetype: any;
    private _count: number = 1;
    private _timeout?: NodeJS.Timeout;
    private _dumped: boolean = false;
    private _tracked: boolean = false;

    get dumped(): boolean { return this._dumped; }

    constructor(error: any, settings: ErrorBucketSettings) {
        this._archetype = error;
        this._settings = settings;

        if (!!this._settings.singleInstance) {
            // Track this instance immediately
            this.track();
        }
        else if (!!this._settings.rateLimitMs) {
            // Create our timeout for dumping the bucket
            this._timeout = setTimeout(this.dump.bind(this), this._settings.rateLimitMs);
        }
    }

    handle(error: any): boolean {
        const handled = (error === this._archetype) ||
            (!!this._archetype.stack && this._archetype.stack === error.stack) ||
            (!!this._archetype.message && this._archetype.message === error.message);

        if (handled) {
            this._count++;
        }

        if (this._settings.rateLimitCount !== undefined && this._count >= this._settings.rateLimitCount) {
            this.dump();
        }

        return handled;
    }

    dump(): void {
        if (!this._dumped) {
            this.track();

            if (!!this._timeout) {
                clearTimeout(this._timeout);
            }

            this._dumped = true;
        }
    }

    private track() {
        if (!this._tracked) {
            Telemetry.trackException(this._archetype as Error, { count: this._count });
            this._tracked = true;
        }
    }
}
